// Models
import { IExerciseData } from 'storage/exercise/models'
import {
  IFormInputs,
  IModelInput,
  IWorkoutSetInput,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'

// React
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'

// Libraries
import { ThemeContext } from 'styled-components'
import { useDispatch } from 'react-redux'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { triggerToastError } from 'storage/general/duck'
import { useModal } from 'hooks'

// Components
import * as Styled from './styled'
import { SeriesFormTag, SpecialSeriesFormTag } from 'components'
import { Aligner, Body, Swipe } from 'heeds-ds'
import { ModalDelete, ModalMoreOptions } from 'components/modals'

// Constants
const TOAST_INFO = {
  bothErrors: {
    customTitle: 'Faltam informações no treino',
    message:
      'Série é um campo obrigatório. Evite treinos vazios, adicione exercícios para revisar.',
    time: 10000,
  },
  emptyWorkout: {
    customTitle: 'Faltam informações no treino',
    message: 'Evite treinos vazios, adicione exercícios para revisar.',
  },
  fieldRequired: {
    customTitle: 'Série é um campo obrigatório!',
    message:
      'Lembre-se de adicionar essa informação no exercício para o seu aluno.',
  },
}

type Props = {
  changePage?: (page: string) => void
  formIndex: number
  onEditSpecialSet?: (workoutSet: TSpecialSetInput) => void
  openExerciseGifModal: (exercise: IExerciseData) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  replicateWorkoutSetFields?: (modelId: string, set: IWorkoutSetInput) => void
  removeWorkoutSet: (modelId: string, workoutSetId: string) => void
  selectedModelIndex?: number
  setSpecialSet: TSetSpecialSet
  stateModels?: IModelInput[]
}

const Mobile: FC<Props> = ({
  replicateWorkoutSetFields,
  changePage,
  formIndex,
  onEditSpecialSet,
  openExerciseGifModal,
  removeExerciseFromWorkoutSet,
  removeWorkoutSet,
  selectedModelIndex,
  setSpecialSet,
  stateModels,
}) => {
  const dispatch = useDispatch()
  const { closeModal, openModal, isVisible } = useModal()
  const theme = useContext(ThemeContext)

  const [selected, setSelected] = useState<string | null>(null)
  const [show, setShow] = useState(false)

  const {
    formState: { errors, isValid },
    unregister,
  } = useFormContext<IFormInputs>()

  const hasModels = stateModels && stateModels.length > 0
  const selectedModelFound =
    hasModels &&
    stateModels.filter((_, index) => index === selectedModelIndex).length > 0

  const onClickTag = (event: React.MouseEvent) => {
    event.preventDefault()
    setShow((prev) => !prev)
  }

  const getCountSets = useCallback(
    (models?: IModelInput[]): number => {
      if (!models) return 0

      const modelSelected =
        selectedModelIndex !== undefined && models[selectedModelIndex]

      if (!modelSelected) return 0

      if (Object.values(modelSelected.workout_set).length === 0) return 0

      return Object.values(modelSelected.workout_set)
        .map((setToExercise) => setToExercise.exercises.length)
        .reduce((accumulator, currentValue) => accumulator + currentValue)
    },
    [selectedModelIndex],
  )

  const countSets = useMemo(
    () => getCountSets(stateModels),
    [getCountSets, stateModels],
  )

  const renderStateModels = () => {
    if (stateModels) {
      return countSets === 1
        ? '1 exercício adicionado'
        : `${+countSets} exercícios adicionados`
    }
    return false
  }

  const handleConfirmModal = () => {
    if (stateModels && selected !== null) {
      const modelId = stateModels[formIndex].id
      const setId = stateModels[formIndex].workout_set[selected].id

      removeWorkoutSet(modelId, setId)
      unregister(`models.${formIndex}.workout_set.${setId}`)
      setSelected(null)
      closeModal()
    }
  }

  const toastType = useMemo(() => {
    let emptyErrors = 0
    let fieldErrors = 0

    errors.models?.forEach?.((fieldModel) => {
      if (fieldModel?.name) return (emptyErrors += 1)
      return (fieldErrors += 1)
    })

    if (fieldErrors && emptyErrors) return 'bothErrors'

    if (emptyErrors) return 'emptyWorkout'

    return 'fieldRequired'
  }, [errors?.models])

  const showToast = useCallback(() => {
    if (!isValid) {
      dispatch(triggerToastError({ ...TOAST_INFO[toastType] }))
    }
  }, [dispatch, isValid, toastType])

  useEffect(() => {
    if (Object.keys(errors).length) showToast()
  }, [errors, showToast])

  return (
    <Styled.Container
      data-testid="workout-sets-mobile-container"
      visible={show}
    >
      <Styled.Header
        data-testid="workout-sets-mobile-header"
        onClick={onClickTag}
      >
        <Styled.Title>{renderStateModels()}</Styled.Title>

        <Styled.ExpandIcon iconName={show ? 'expandLess' : 'expandMore'} />
      </Styled.Header>

      <Styled.WorkoutSets data-testid="workout-sets-mobile-list">
        {!hasModels ||
        !selectedModelFound ||
        Object.values(
          stateModels.filter((_, index) => index === selectedModelIndex)[0]
            .workout_set,
        ).length === 0 ? (
          <Aligner justify="center" padding="24px">
            <Body type="copy3" color={theme.colors.text.subdued} align="center">
              Você pode criar o seu treino do zero clicando nos exercícios
              abaixo.
            </Body>
          </Aligner>
        ) : (
          hasModels &&
          stateModels.map((stateModel, modelIndex) => {
            return Object.values(stateModel.workout_set).map((stateSet) => {
              const prefixName = `models.${modelIndex}.workout_set.${stateSet.id}`

              if (stateSet.type === 'normal') {
                return (
                  <SeriesFormTag
                    replicateWorkoutSetFields={(set) =>
                      replicateWorkoutSetFields?.(stateModel.id, set)
                    }
                    key={stateSet.id}
                    exercise={stateSet?.exercises[0]?.exercise}
                    name={prefixName}
                    onRemove={() => {
                      setSelected(stateSet.id)
                      openModal('delete-workout-sets-mobile-modal')
                    }}
                    onClickImage={openExerciseGifModal}
                    visible={show && modelIndex === selectedModelIndex}
                  />
                )
              }

              return (
                <Swipe
                  key={stateSet.id}
                  active
                  actions={[
                    {
                      icon: 'moreHorizontal',
                      color: theme.colors.interactive.hovered,
                      onClick: () => {
                        setSelected(stateSet.id)
                        openModal('workout-sets-mobile-modal')
                      },
                    },
                  ]}
                  shadow={show && modelIndex === selectedModelIndex}
                >
                  <SpecialSeriesFormTag
                    replicateWorkoutSetFields={(set) =>
                      replicateWorkoutSetFields?.(stateModel.id, set)
                    }
                    setSpecialSet={setSpecialSet}
                    key={stateSet.id}
                    onClickImage={openExerciseGifModal}
                    onEdit={onEditSpecialSet}
                    name={prefixName}
                    removeWorkoutSet={() => {
                      setSelected(stateSet.id)
                      openModal('delete-workout-sets-mobile-modal')
                    }}
                    removeExerciseFromSpecialWorkoutSet={(exerciseIndex) => {
                      if (stateModels) {
                        const modelId = stateModels[formIndex].id
                        const setId = stateSet.id
                        removeExerciseFromWorkoutSet(
                          modelId,
                          setId,
                          exerciseIndex,
                        )
                        unregister(
                          `models.${formIndex}.workout_set.${setId}.exercises.${exerciseIndex}`,
                        )
                      }
                    }}
                    visible={show && modelIndex === selectedModelIndex}
                    workoutSet={stateSet}
                  />
                </Swipe>
              )
            })
          })
        )}
      </Styled.WorkoutSets>

      <Styled.GradientWhite>
        <Styled.SaveButton
          track={buttonClickTracking}
          trackName="update_state_and_navigate_to_workout_review"
        >
          Salvar treino
        </Styled.SaveButton>
      </Styled.GradientWhite>

      {isVisible === 'workout-sets-mobile-modal' && (
        <ModalMoreOptions
          options={[
            {
              icon: 'edit',
              label: 'Editar série especial',
              onClick: () => {
                if (stateModels && selected !== null) {
                  const setForModal =
                    stateModels[formIndex].workout_set[selected]
                  const editSpecialSet = {
                    ...setForModal,
                    exercises: setForModal.exercises.reduce(
                      (acc, setToExercise) => ({
                        ...acc,
                        [setToExercise.id]: setToExercise,
                      }),
                      {},
                    ),
                    index: 0,
                  }
                  setSpecialSet(editSpecialSet)
                  onEditSpecialSet?.(editSpecialSet)
                  changePage?.('3')
                  closeModal()
                }
              },
            },
            {
              icon: 'delete',
              label: 'Excluir',
              color: theme.colors.text.critical,
              onClick: () => {
                openModal('delete-workout-sets-mobile-modal')
              },
            },
          ]}
        />
      )}

      {isVisible === 'delete-workout-sets-mobile-modal' && (
        <ModalDelete
          confirmTitle="Excluir"
          description="Tem certeza que deseja excluir este exercício?"
          onConfirm={handleConfirmModal}
          title="Excluir Exercício"
        />
      )}
    </Styled.Container>
  )
}

export default Mobile
